/**
 * AddonsManager — Sprint T1
 *
 * Organiser/Admin management UI for event add-ons (Extras): parking, meals,
 * merch, donations, VIP upgrades, sponsor items. Rendered inside the event
 * edit form (organiser) and the Admin events panel (admin — same endpoints,
 * admin may manage any event).
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Archive, ParkingCircle, UtensilsCrossed, Shirt, HeartHandshake, Crown, Handshake, Package } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { addonsApi, centsToAUD } from "../lib/events";
import { extractError } from "../lib/api";

export const ADDON_CATEGORIES = [
  { value: "parking", label: "Parking", icon: ParkingCircle },
  { value: "meal", label: "Meal", icon: UtensilsCrossed },
  { value: "merch", label: "Merchandise", icon: Shirt },
  { value: "donation", label: "Donation", icon: HeartHandshake },
  { value: "vip_upgrade", label: "VIP upgrade", icon: Crown },
  { value: "sponsor", label: "Sponsor / support", icon: Handshake },
  { value: "other", label: "Other", icon: Package },
];

export const categoryMeta = (value) =>
  ADDON_CATEGORIES.find((c) => c.value === value) || ADDON_CATEGORIES[6];

const EMPTY_FORM = {
  name: "",
  description: "",
  category: "parking",
  pricing_type: "fixed",
  price_aud: "0.00",
  min_amount_aud: "1.00",
  suggested_amounts: "",
  gst_treatment: "taxable",
  gift_confirmed: false,
  scope: "per_order",
  quantity_total: "",
  max_per_order: "10",
  status: "active",
};

function addonToForm(a) {
  return {
    name: a.name,
    description: a.description || "",
    category: a.category,
    pricing_type: a.pricing_type,
    price_aud: ((a.price_cents || 0) / 100).toFixed(2),
    min_amount_aud: ((a.min_amount_cents || 100) / 100).toFixed(2),
    suggested_amounts: (a.suggested_amounts_cents || []).map((c) => (c / 100).toFixed(2)).join(", "),
    gst_treatment: a.gst_treatment || "taxable",
    gift_confirmed: !!a.gift_confirmed,
    scope: a.scope,
    quantity_total: a.quantity_total == null ? "" : String(a.quantity_total),
    max_per_order: String(a.max_per_order || 10),
    status: a.status,
  };
}

function formToPayload(f) {
  const audToC = (v) => Math.round(parseFloat(v || "0") * 100) || 0;
  const suggested = f.suggested_amounts
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)
    .map(audToC)
    .filter((c) => c >= 100);
  return {
    name: f.name.trim(),
    description: f.description.trim() || null,
    category: f.category,
    pricing_type: f.pricing_type,
    price_cents: f.pricing_type === "fixed" ? audToC(f.price_aud) : 0,
    min_amount_cents: f.pricing_type === "buyer_chooses" ? Math.max(100, audToC(f.min_amount_aud)) : 100,
    suggested_amounts_cents: f.pricing_type === "buyer_chooses" && suggested.length ? suggested : null,
    gst_treatment: f.gst_treatment,
    gift_confirmed: f.gift_confirmed,
    scope: f.scope,
    quantity_total: f.quantity_total === "" ? null : Math.max(1, parseInt(f.quantity_total, 10) || 1),
    max_per_order: Math.max(1, parseInt(f.max_per_order, 10) || 10),
    status: f.status,
  };
}

export default function AddonsManager({ eventId }) {
  const [addons, setAddons] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // addon being edited (null = create)
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setAddons(await addonsApi.list(eventId));
    } catch (err) {
      toast.error(extractError(err));
      setAddons([]);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm(addonToForm(a)); setDialogOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required."); return; }
    if (form.gst_treatment === "gst_free_genuine_gift" && !form.gift_confirmed) {
      toast.error("Please confirm the buyer receives no material benefit to mark this GST-free.");
      return;
    }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editing) await addonsApi.update(editing.id, payload);
      else await addonsApi.create(eventId, payload);
      toast.success(editing ? "Add-on updated" : "Add-on created");
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  };

  const archive = async (a) => {
    try {
      await addonsApi.archive(a.id);
      toast.success(`"${a.name}" archived`);
      await load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));
  const isDonationLike = form.pricing_type === "buyer_chooses";

  return (
    <div data-testid="addons-manager">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-[var(--gt-ink-2)] max-w-md">
          Optional extras buyers can add at checkout — parking, meals, merch, donations,
          VIP upgrades. No platform booking fee applies to add-ons.
        </p>
        <button type="button" onClick={openCreate} className="gt-btn-primary text-xs shrink-0" data-testid="addon-create-btn">
          <Plus size={14} /> New add-on
        </button>
      </div>

      {addons === null ? (
        <div className="py-8 flex justify-center"><Loader2 className="animate-spin" size={18} /></div>
      ) : addons.length === 0 ? (
        <div className="border border-dashed border-[var(--gt-line)] rounded-sm py-8 text-center text-sm text-[var(--gt-ink-3)]" data-testid="addons-empty">
          No add-ons yet.
        </div>
      ) : (
        <div className="divide-y divide-[var(--gt-line)] border border-[var(--gt-line)] rounded-sm bg-white">
          {addons.map((a) => {
            const Meta = categoryMeta(a.category);
            return (
              <div key={a.id} className="flex items-center gap-3 px-4 py-3" data-testid={`addon-row-${a.id}`}>
                <Meta.icon size={16} className="text-[var(--gt-accent)] shrink-0" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                    {a.name}
                    <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[var(--gt-line)] rounded-sm text-[var(--gt-ink-3)]">{Meta.label}</span>
                    {a.status === "hidden" && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-[var(--gt-ink)] text-white rounded-sm">Hidden</span>}
                    {a.gst_treatment === "gst_free_genuine_gift" && <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[var(--gt-line)] rounded-sm text-[var(--gt-ink-3)]">GST-free gift</span>}
                  </div>
                  <div className="text-xs text-[var(--gt-ink-2)] font-mono">
                    {a.pricing_type === "buyer_chooses"
                      ? `Buyer chooses · min ${centsToAUD(a.min_amount_cents)}`
                      : a.price_cents === 0 ? "Free" : centsToAUD(a.price_cents)}
                    {" · "}{a.scope === "per_ticket" ? "per ticket" : "per order"}
                    {" · sold "}{a.quantity_sold}
                    {a.quantity_total != null ? ` / ${a.quantity_total} (${a.available} left)` : " · unlimited"}
                  </div>
                </div>
                <button type="button" onClick={() => openEdit(a)} className="text-xs inline-flex items-center gap-1 hover:underline" data-testid={`addon-edit-${a.id}`}>
                  <Pencil size={12} /> Edit
                </button>
                <button type="button" onClick={() => archive(a)} className="text-xs inline-flex items-center gap-1 text-[var(--gt-accent)] hover:underline" data-testid={`addon-archive-${a.id}`}>
                  <Archive size={12} /> Archive
                </button>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="addon-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit — ${editing.name}` : "New add-on"}</DialogTitle>
            <DialogDescription>Prices are GST-inclusive AUD. Add-ons carry no platform booking fee.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="gt-form-label">Name</label>
              <input className="gt-input" value={form.name} onChange={upd("name")} maxLength={120} placeholder="e.g. Car Park Pass" data-testid="addon-name-input" />
            </div>
            <div>
              <label className="gt-form-label">Description (optional)</label>
              <input className="gt-input" value={form.description} onChange={upd("description")} maxLength={500} data-testid="addon-desc-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="gt-form-label">Category</label>
                <select className="gt-input" value={form.category} onChange={upd("category")} data-testid="addon-category-select">
                  {ADDON_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="gt-form-label">Pricing</label>
                <select className="gt-input" value={form.pricing_type} onChange={upd("pricing_type")} data-testid="addon-pricing-select">
                  <option value="fixed">Fixed price</option>
                  <option value="buyer_chooses">Buyer chooses amount</option>
                </select>
              </div>
            </div>
            {!isDonationLike ? (
              <div>
                <label className="gt-form-label">Price (AUD)</label>
                <input className="gt-input" type="number" min="0" step="0.01" value={form.price_aud} onChange={upd("price_aud")} data-testid="addon-price-input" />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="gt-form-label">Minimum (AUD)</label>
                  <input className="gt-input" type="number" min="1" step="0.01" value={form.min_amount_aud} onChange={upd("min_amount_aud")} data-testid="addon-min-input" />
                </div>
                <div>
                  <label className="gt-form-label">Suggested (comma-sep AUD)</label>
                  <input className="gt-input" value={form.suggested_amounts} onChange={upd("suggested_amounts")} placeholder="5, 10, 25" data-testid="addon-suggested-input" />
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="gt-form-label">Scope</label>
                <select className="gt-input" value={form.scope} onChange={upd("scope")} data-testid="addon-scope-select">
                  <option value="per_order">Per order</option>
                  <option value="per_ticket">Per ticket (max 1 each)</option>
                </select>
              </div>
              <div>
                <label className="gt-form-label">Max per order</label>
                <input className="gt-input" type="number" min="1" max="100" value={form.max_per_order} onChange={upd("max_per_order")} data-testid="addon-maxper-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="gt-form-label">Inventory (blank = unlimited)</label>
                <input className="gt-input" type="number" min="1" value={form.quantity_total} onChange={upd("quantity_total")} placeholder="Unlimited" data-testid="addon-inventory-input" />
              </div>
              <div>
                <label className="gt-form-label">GST treatment</label>
                <select className="gt-input" value={form.gst_treatment} onChange={upd("gst_treatment")} data-testid="addon-gst-select">
                  <option value="taxable">Taxable (GST-inclusive)</option>
                  <option value="gst_free_genuine_gift">GST-free genuine gift</option>
                </select>
              </div>
            </div>
            {form.gst_treatment === "gst_free_genuine_gift" && (
              <label className="flex items-start gap-2 text-xs cursor-pointer border border-[var(--gt-line)] rounded-sm p-3 bg-[var(--gt-bg-surface)]" data-testid="addon-gift-confirm-label">
                <input type="checkbox" checked={form.gift_confirmed} onChange={upd("gift_confirmed")} className="mt-0.5" data-testid="addon-gift-confirm-checkbox" />
                <span>I confirm the buyer receives <strong>no material benefit</strong> in return for this payment (genuine gift). GST will not be calculated on this item.</span>
              </label>
            )}
            {editing && (
              <div>
                <label className="gt-form-label">Status</label>
                <select className="gt-input" value={form.status} onChange={upd("status")} data-testid="addon-status-select">
                  <option value="active">Active (visible to buyers)</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-1">
              <button type="button" className="gt-btn-ghost text-sm" onClick={() => setDialogOpen(false)}>Cancel</button>
              <button type="button" className="gt-btn-primary text-sm" onClick={save} disabled={saving} data-testid="addon-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                {editing ? "Save changes" : "Create add-on"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
